import { cn } from '@/lib/utils';
import { forwardRef, Ref, useEffect, useMemo, useRef } from 'react';
import HlsVideo from 'hls-video-element/react';
import { MonitorAlertEventDto, MonitoringEventValueType } from '../../types';
import { VideoStreamType } from '@/lib/api/types.gen';

export type VideoPlayerBaseProps = {
  onLoad?: () => void;
  controls?: boolean;
  autoPlay?: boolean;
  muted?: boolean;
  className?: string;
};

const guessEventTypeFromUrl = (url: string) => {
  if (url.includes('stream.watchful.cloud')) {
    return {
      type: MonitoringEventValueType.PreviewVideoMp4,
      sourceUrl: url,
    };
  }

  // Extract from the url
  const extension = /\.(?<Extension>[\w\d]+)(?:\?.*)?$/g.exec(url?.toLowerCase() || '')?.groups
    ?.Extension;

  const type = {
    gif: MonitoringEventValueType.PreviewVideo,
    mp4: MonitoringEventValueType.PreviewVideoMp4,
    m3u8: MonitoringEventValueType.PreviewVideoHls,
  }[extension || ''];

  if (type) {
    return {
      type,
      sourceUrl: url,
    };
  }

  if (!type && url) {
    return {
      type: MonitoringEventValueType.PreviewVideoLink,
      sourceUrl: url,
    };
  }
};

export type VideoPlayerLegacyProps = VideoPlayerBaseProps & {
  event?: MonitorAlertEventDto;
  previewUrl?: string;
  videoUrl?: string;
  videoProcessor?: string;
  onIsFullWidthPlayer?: () => void;
};

const VideoPlayerLegacy_Inner = (
  {
    event,
    previewUrl,
    onLoad,
    videoUrl,
    videoProcessor,
    controls = true,
    autoPlay = true,
    muted = true,
    className,
    onIsFullWidthPlayer,
  }: VideoPlayerLegacyProps,
  ref: Ref<HTMLVideoElement>
) => {
  const value = useMemo(() => {
    const eventType = event?.values?.find(
      (v) =>
        v.type === MonitoringEventValueType.PreviewVideo ||
        v.type === MonitoringEventValueType.PreviewVideoMp4 ||
        v.type === MonitoringEventValueType.PreviewVideoLink ||
        v.type === MonitoringEventValueType.PreviewVideoHls
    );

    if (eventType) {
      return eventType;
    }

    // Guess from the url
    return guessEventTypeFromUrl(previewUrl || '');
  }, [event?.values, previewUrl]);

  // const sourceUrl = useMemo(() => {
  //   if (!value?.sourceUrl) return '';
  //   if (videoProcessor !== 'OpenEye') return value.sourceUrl;

  //   return value.sourceUrl.replace(/([?&])time=\d+/, ``);
  // }, [value?.sourceUrl, videoProcessor]);

  const eventPlaybackUrl = useMemo(() => {
    if (!previewUrl) return '';
    if (videoProcessor !== 'OpenEye') return previewUrl;

    const url = new URL(previewUrl);
    const timeParam = url.searchParams.get('time');

    if (timeParam) {
      const time = parseInt(timeParam);
      const startTime = Math.max(0, time - 5_000_000); // 5 seconds before in microseconds
      const etime = time + 15_000_000; // 15 seconds after in microseconds

      url.searchParams.set('time', startTime.toString());
      url.searchParams.set('etime', etime.toString());
    }

    return url.toString();
  }, [previewUrl]);

  const isFullWidthPlayer =
    value?.type === MonitoringEventValueType.PreviewVideo ||
    value?.type === MonitoringEventValueType.PreviewVideoLink;

  useEffect(() => {
    if (isFullWidthPlayer) {
      onIsFullWidthPlayer?.();
    }
  }, [onIsFullWidthPlayer, isFullWidthPlayer]);

  if (value?.type === MonitoringEventValueType.PreviewVideoLink) {
    if (videoProcessor === 'OpenEye') {
      return (
        <video
          src={eventPlaybackUrl}
          className={cn('h-full w-full border-0 max-h-[calc(100vh-340px)]', className)}
          onLoad={onLoad}
          autoPlay={autoPlay}
          muted={muted}
          controls={controls}
          loop
        />
      );
    } else {
      return (
        <iframe
          src={previewUrl}
          className={cn('h-full w-full border-0 max-h-[calc(100vh-340px)]', className)}
          onLoad={onLoad}
          allowFullScreen
        />
      );
    }
  }

  if (value?.type === MonitoringEventValueType.PreviewVideo) {
    return (
      <img
        src={previewUrl}
        className={cn('h-full w-full border-0 object-contain max-h-[calc(100vh-340px)]', className)}
        onLoad={onLoad}
      />
    );
  }

  if (value?.type === MonitoringEventValueType.PreviewVideoHls || videoUrl?.includes('.m3u8')) {
    return (
      <HlsVideo
        src={videoUrl}
        className={cn('h-full w-full object-scale-down object-top max-h-[calc(100vh-340px)]', className)}
        loop
        playsInline
        muted={muted}
        controls={controls}
        onLoadedData={onLoad}
      />
    );
  }

  if (videoUrl || value?.type === MonitoringEventValueType.PreviewVideoMp4) {
    return (
      <video
        ref={ref}
        src={previewUrl || videoUrl}
        className={cn('h-full w-full object-scale-down object-top max-h-[calc(100vh-340px)]', className)}
        autoPlay={autoPlay}
        loop
        playsInline
        muted={muted}
        controls={controls}
        onLoadedData={onLoad}
      />
    );
  }

  return (
    <div className={cn('flex h-full items-center justify-center', className)}>
      <p className="text-gray-500">No video available</p>
    </div>
  );
};

export const VideoPlayerLegacy = forwardRef(VideoPlayerLegacy_Inner);

export interface VideoPlayerProps extends VideoPlayerBaseProps {
  url: string;
  urlType: VideoStreamType;
  className?: string;
}

const VideoPlayer_Inner = (
  {
    url,
    urlType,
    onLoad,
    className,
    controls = true,
    autoPlay = true,
    muted = true,
  }: VideoPlayerProps,
  ref: Ref<HTMLVideoElement>
) => {
  switch (urlType) {
    case VideoStreamType.Embedded:
      return (
        <iframe
          src={url}
          className={cn('h-full w-full', className)}
          onLoad={onLoad}
          allowFullScreen
        />
      );

    case VideoStreamType.Mp4:
    case VideoStreamType.MxPeg:
      return (
        <video
          ref={ref}
          src={url}
          className={cn('h-full w-full', className)}
          autoPlay={autoPlay}
          muted={muted}
          controls={controls}
          onLoadedData={onLoad}
        />
      );

    case VideoStreamType.Hls:
      return (
        <HlsVideo
          src={url}
          className={cn('h-full w-full', className)}
          autoplay={autoPlay}
          loop
          playsInline
          muted={muted}
          controls={controls}
          onLoadedData={onLoad}
        />
      );

    case VideoStreamType.Rtsp:
    case VideoStreamType.Mjpeg:
      return (
          <video
            ref={ref}
            src={url}
            className={cn('h-full w-full', className)}
            autoPlay={autoPlay}
            muted={muted}
            controls={controls}
            onLoadedData={onLoad}
          />
      );
  }
};

/** VideoPlayer for v2 streams */
export const VideoPlayer = forwardRef(VideoPlayer_Inner);

export interface VideoPlayerCompatProps extends VideoPlayerBaseProps {
  url?: string;
  urlType?: VideoStreamType;
  previewUrl?: string;
  videoProcessor?: string;
  onIsFullWidthPlayer?: () => void;
}

const VideoPlayerCompat_Inner = (props: VideoPlayerCompatProps, ref: Ref<HTMLVideoElement>) => {
  const {
    url,
    previewUrl,
    urlType,
    videoProcessor,
    controls = true,
    autoPlay = true,
    muted = true,
    onLoad,
    className,
    onIsFullWidthPlayer,
  } = props;

  if (!previewUrl && !url) {
    return (
      <div className="relative flex h-[calc(100vh-360px)] w-full flex-col items-center justify-center gap-4">
        <p className="text-lg font-bold text-gray-500">No Preview Available</p>
      </div>
    );
  }

  if (previewUrl || !urlType) {
    return (
      <VideoPlayerLegacy
        ref={ref}
        previewUrl={previewUrl}
        controls={controls}
        autoPlay={autoPlay}
        muted={muted}
        videoProcessor={videoProcessor}
        onLoad={onLoad}
        className={className}
        onIsFullWidthPlayer={onIsFullWidthPlayer}
      />
    );
  }

  if (!url || !urlType) {
    return (
      <div className="relative flex h-[calc(100vh-360px)] w-full flex-col items-center justify-center gap-4">
        <p className="text-lg font-bold text-gray-500">No Video Available</p>
      </div>
    );
  }

  return (
    <VideoPlayer
      ref={ref}
      url={url}
      urlType={urlType}
      controls={controls}
      autoPlay={autoPlay}
      muted={muted}
      onLoad={onLoad}
      className={className}
    />
  );
};

/** Routes between video player and legacy player, with error handling and spinners. */
export const VideoPlayerCompat = forwardRef(VideoPlayerCompat_Inner);
